/**
 * CIVIK — Textes du site vitrine (refonte 2026-09-15).
 * ---------------------------------------------------------------------------
 * SOURCE : cahier des charges « Refonte UX/UI, SEO, marketing et conformité de
 * CIVIK.tech » (Marc, 15/09/2026) puis « Correction immédiate de CIVIK.tech »
 * (Marc, 15/09/2026 soir) : les blocs « TEXTE PUBLIC À AFFICHER » sont
 * DÉFINITIFS et repris mot pour mot ; les autres textes sont rédigés pour
 * l'audience concernée (citoyen, professionnel candidat / partenaire,
 * collectivité) selon la structure : ce que fait CIVIK → ce que cela apporte →
 * action disponible. Aucune promesse, statistique ou partenariat inventé.
 * Sections conservées de l'ancien site : Abonnement, Cas d'usage, Télécharger.
 */

export const SITE_URL = 'https://civik.tech';
export const CONTACT_EMAIL = 'contact@civik.tech';
export const DPO_EMAIL = 'dpo@civik.tech';

/* CTA principal : l'application CIVIK en production = l'espace web
   (connexion → webapp /urgence). Décision Marc 15/09. */
export const APP_URL = '/connexion';

export const NAV = [
  { label: 'Comment ça marche', href: '/#comment-ca-marche' },
  { label: 'Entraide', href: '/#entraide' },
  { label: 'Sécurité', href: '/#securite' },
  { label: 'Collectivités', href: '/#collectivites' },
  { label: 'Données & confidentialité', href: '/#donnees-confidentialite' },
  { label: 'FAQ', href: '/#faq' },
];

export const HERO = {
  h1: 'Ensemble, nous sommes moins seuls.',
  sub: "CIVIK est une application d'entraide et de sécurité citoyenne qui permet de demander de l'aide, d'alerter autour de soi et de documenter une situation en quelques secondes.",
  ctaPrimary: 'UTILISER CIVIK',
  ctaSecondary: 'DÉCOUVRIR COMMENT ÇA MARCHE',
  markers: ['Entraide de proximité', 'Géolocalisation', 'Alerte en temps réel', 'Respect de la vie privée'],
};

export const WHY = {
  title: 'Personne ne devrait se retrouver seul face à une situation difficile.',
  p1: "Chaque jour, nous croisons des centaines de personnes. CIVIK transforme cette proximité en capacité d'entraide.",
  p2: "Demander de l'aide, signaler une situation, documenter ce qui se passe ou simplement être présent lorsqu'une personne à proximité a besoin d'assistance : CIVIK met la technologie au service de la solidarité entre citoyens.",
  highlight: 'Aider ne signifie jamais se mettre en danger.',
};

export const SITUATIONS = {
  title: "Quand CIVIK peut-il m'aider ?",
  cards: [
    { icon: 'shield', title: 'Je me sens en danger', text: 'Je peux rapidement déclencher les fonctions de sécurité prévues par CIVIK et documenter la situation.' },
    { icon: 'eye', title: "Je suis témoin d'une situation", text: 'Je peux documenter ce qui se passe et contribuer utilement, sans me mettre en danger.' },
    { icon: 'pin', title: "Quelqu'un près de moi demande de l'aide", text: "CIVIK peut me permettre de savoir qu'une personne à proximité sollicite de l'assistance." },
    { icon: 'hands', title: 'Je veux contribuer', text: "Je peux participer à un réseau d'entraide de proximité et être utile lorsque les circonstances le permettent." },
  ],
};

export const HOW = {
  id: 'comment-ca-marche',
  title: 'Simple quand chaque seconde compte.',
  steps: [
    { num: '1', label: 'DEMANDER', text: "Déclenchez CIVIK lorsque vous avez besoin d'aide." },
    { num: '2', label: 'ALERTER', text: "Les fonctionnalités prévues par CIVIK permettent d'alerter autour de vous selon la situation et les autorisations activées." },
    { num: '3', label: 'DOCUMENTER', text: 'Lorsque la fonction est activée, CIVIK permet de documenter la situation afin de conserver les informations utiles.' },
    { num: '4', label: "S'ENTRAIDER", text: "Les personnes informées peuvent apporter leur aide lorsqu'elles peuvent le faire sans danger." },
  ],
  note: "CIVIK encourage l'entraide responsable. Ne vous exposez jamais à un danger pour intervenir. En présence d'une urgence nécessitant l'intervention des services compétents, utilisez les numéros d'urgence appropriés.",
};

export const ALERT = {
  id: 'securite',
  title: 'En cas de danger, chaque seconde compte.',
  text: "CIVIK permet d'accéder rapidement aux fonctions de sécurité disponibles dans l'application.",
  steps: [
    "Déclenchez l'alerte",
    'CIVIK active les fonctions que vous avez autorisées',
    'La situation peut être documentée',
    'Les personnes concernées peuvent être informées',
    "Vous gardez accès aux services publics d'urgence",
  ],
  disclaimer: "CIVIK complète les dispositifs publics de secours et de sécurité. L'application ne remplace ni la police, ni la gendarmerie, ni les services de secours.",
};

export const EMERGENCY = {
  title: 'Une urgence nécessite les services compétents ?',
  numbers: [
    { label: 'Police / Gendarmerie', number: '17', tel: 'tel:17' },
    { label: 'Pompiers', number: '18', tel: 'tel:18' },
    { label: 'Urgence européenne', number: '112', tel: 'tel:112' },
  ],
  text: "CIVIK n'est pas un service public d'urgence. En cas d'urgence nécessitant l'intervention de la police, de la gendarmerie ou des secours, utilisez les numéros d'urgence appropriés.",
};

export const MUTUAL_AID = {
  id: 'entraide',
  title: 'La proximité devient une force.',
  p1: "Nous sommes rarement réellement seuls. Dans une rue, un quartier, un commerce, une gare ou un lieu public, d'autres personnes sont souvent à proximité.",
  p2: "CIVIK permet de mettre cette proximité au service de l'entraide, sans demander à quiconque de prendre des risques inutiles.",
  principles: [
    { icon: 'bell', title: 'Être informé', text: "Savoir qu'une personne à proximité peut avoir besoin d'aide." },
    { icon: 'hands', title: 'Être utile', text: 'Apporter une assistance adaptée lorsque les circonstances le permettent.' },
    { icon: 'check', title: 'Rester responsable', text: "Ne jamais se mettre en danger et faire appel aux services compétents lorsque la situation l'exige." },
  ],
};

export const VIDEO = {
  title: 'Documenter une situation quand cela compte.',
  p1: "Selon les fonctionnalités activées et les autorisations accordées, CIVIK peut permettre d'enregistrer des images et, le cas échéant, du son afin de documenter une situation.",
  p2: "Le champ de la caméra peut inclure des personnes, des visages, des véhicules, des lieux et d'autres éléments présents dans l'environnement de l'utilisateur.",
  p3: "CIVIK vous explique, dans l'application et dans sa politique de confidentialité, comment ces données sont utilisées, à qui elles peuvent être transmises et combien de temps elles sont conservées.",
  cta: "COMPRENDRE L'UTILISATION DE MES DONNÉES",
  ctaHref: '/confidentialite-donnees',
};

export const COLLECTIVITES = {
  id: 'collectivites',
  title: 'CIVIK pour les collectivités',
  subtitle: "Une solution d'entraide et de sécurité citoyenne au service des territoires.",
  text: "CIVIK permet aux collectivités de disposer d'un réseau local d'entraide citoyenne et d'intégrer de nouveaux outils numériques à leur politique de proximité, de prévention et de tranquillité publique.",
  benefits: [
    { icon: 'network', title: "Disposer d'un réseau local d'entraide citoyenne", text: 'Mettre la proximité et la solidarité entre habitants au service du territoire.' },
    { icon: 'bell', title: 'Améliorer la diffusion des alertes', text: "Faciliter la circulation d'informations utiles lorsque les dispositifs déployés le permettent." },
    { icon: 'chart', title: 'Structurer les remontées du terrain', text: "Disposer d'informations permettant de mieux comprendre certaines situations rencontrées sur le territoire, dans le respect du cadre juridique applicable." },
    { icon: 'puzzle', title: 'Compléter les dispositifs existants', text: 'CIVIK a vocation à compléter les politiques et outils locaux de prévention, de proximité et de tranquillité publique, sans se substituer aux services compétents.' },
  ],
  ctaPrimary: 'DÉCOUVRIR CIVIK POUR MA COLLECTIVITÉ',
  ctaPrimaryHref: '/collectivites',
  ctaSecondary: 'CONTACTER CIVIK',
  ctaSecondaryHref: '/collectivites#formulaire',
};

export const DATA = {
  id: 'donnees-confidentialite',
  title: 'Vos données, en toute transparence.',
  p1: "Certaines fonctionnalités de CIVIK utilisent votre localisation, la caméra ou le microphone de votre appareil pour vous permettre d'alerter, de documenter une situation ou de bénéficier des services proposés.",
  p2: "Vous êtes informé de l'utilisation de ces données et des autorisations nécessaires directement dans CIVIK. Vous pouvez ainsi comprendre quelles données sont utilisées et dans quel but.",
  commitments: [
    { icon: 'eye', title: 'Transparence', text: 'CIVIK vous informe des données utilisées et de leur finalité.' },
    { icon: 'sliders', title: 'Maîtrise', text: 'Les autorisations nécessaires vous sont présentées au moment où elles sont utiles.' },
    { icon: 'minimize', title: 'Données utiles', text: 'CIVIK utilise les données nécessaires au fonctionnement des services activés.' },
    { icon: 'lock', title: 'Sécurité', text: 'Les données sont protégées par des mesures adaptées à leur nature et à leur sensibilité.' },
  ],
  cta: 'COMPRENDRE COMMENT CIVIK UTILISE MES DONNÉES',
  ctaHref: '/confidentialite-donnees',
};

export const RESPONSIBILITY = {
  title: 'Aider, sans se mettre en danger.',
  main: 'Recevoir une alerte CIVIK ne signifie jamais devoir intervenir physiquement.',
  text: "Selon la situation, aider peut simplement consister à observer à distance, alerter les services compétents, transmettre une information utile ou rester disponible.",
  strong: 'Votre sécurité reste prioritaire.',
};

export const FAQ = {
  id: 'faq',
  title: 'Questions fréquentes',
  items: [
    { q: 'CIVIK remplace-t-il le 17, le 18 ou le 112 ?', a: "Non. CIVIK est une application d'entraide et de sécurité citoyenne. Elle ne remplace ni la police, ni la gendarmerie, ni les services de secours. Lorsqu'une situation nécessite leur intervention, utilisez les numéros d'urgence appropriés." },
    { q: "Suis-je obligé d'intervenir lorsque je reçois une alerte ?", a: "Non. CIVIK ne demande jamais à un utilisateur de se mettre en danger. Vous restez libre d'agir en fonction de la situation et de vos possibilités." },
    { q: 'Dois-je intervenir physiquement ?', a: "Non. Une aide utile peut consister à rester à distance, observer, alerter les services compétents ou transmettre une information. N'intervenez jamais physiquement si cela peut vous mettre en danger ou aggraver la situation." },
    { q: 'CIVIK peut-il utiliser ma position ?', a: "Oui, pour certaines fonctionnalités qui nécessitent l'accès à votre localisation. CIVIK vous présente, dans l'application et dans sa politique de confidentialité, les conditions d'utilisation de cette donnée et les autorisations correspondantes." },
    { q: 'CIVIK peut-il enregistrer une vidéo ?', a: "Selon les fonctionnalités utilisées et les autorisations accordées, CIVIK peut permettre de documenter une situation à l'aide de la caméra et, le cas échéant, du microphone de votre appareil." },
    { q: "D'autres personnes peuvent-elles apparaître dans une vidéo ?", a: "Oui. Le champ de la caméra peut inclure des personnes présentes autour de l'utilisateur ainsi que d'autres éléments de l'environnement. Les conditions de traitement de ces données sont précisées dans la politique de confidentialité." },
    { q: "Qui peut recevoir les informations liées à une alerte ?", a: "Les informations liées à une alerte sont transmises aux destinataires prévus par le service CIVIK utilisé et selon les fonctionnalités activées. Lorsqu'un dispositif CIVIK est déployé avec une collectivité ou un service compétent, les informations prévues par ce dispositif peuvent également lui être transmises.", a2: "Les destinataires et les conditions de transmission sont précisés dans CIVIK et dans les informations relatives à la protection des données." },
    { q: 'Une collectivité peut-elle utiliser CIVIK ?', a: "Oui. CIVIK peut être proposé aux collectivités souhaitant disposer d'un réseau local d'entraide citoyenne et compléter leurs dispositifs de proximité, de prévention et de tranquillité publique." },
  ],
};

export const FINAL_CTA = {
  title: 'Ensemble, nous sommes moins seuls.',
  text: "Rejoignez CIVIK et faites de la proximité une force d'entraide.",
  ctaPrimary: 'UTILISER CIVIK',
  ctaSecondary: 'CIVIK POUR MA COLLECTIVITÉ',
  ctaSecondaryHref: '/collectivites',
};

export const FOOTER = {
  brand: 'CIVIK',
  tagline: 'Entraide & sécurité citoyenne',
  nav: [
    { label: 'Comment ça marche', href: '/#comment-ca-marche' },
    { label: 'Entraide', href: '/#entraide' },
    { label: 'Sécurité', href: '/#securite' },
    { label: 'Collectivités', href: '/collectivites' },
    { label: 'Données & confidentialité', href: '/confidentialite-donnees' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact', href: '/contact' },
  ],
  legal: [
    { label: 'Mentions légales', href: '/mentions-legales' },
    { label: "Conditions Générales d'Utilisation", href: '/cgu' },
    { label: 'Conditions Générales de Vente', href: '/cgv' },
    { label: 'Politique de confidentialité', href: '/politique-confidentialite' },
    { label: 'Gestion des cookies', href: '/politique-cookies' },
    { label: 'Données personnelles', href: '/confidentialite-donnees' },
    { label: 'Accessibilité', href: '/accessibilite' },
  ],
  /* Sections de l'ancien site conservées (décision Marc 15/09) */
  product: [
    { label: 'Abonnement', href: '/#abonnement' },
    { label: 'Professionnels', href: '/#professionnels' },
    { label: "Cas d'usage", href: '/#cas-usage' },
    { label: "Télécharger l'application", href: '/#telecharger' },
    { label: 'Espace utilisateur', href: '/connexion' },
  ],
};

/* ── Sections conservées de l'ancien site (textes publiés au 14/09/2026) ── */

export const PRICING = {
  id: 'abonnement',
  title: 'Abonnement',
  sub: 'Les fonctions essentielles sont gratuites.',
  free: {
    name: 'Gratuit', price: '0€', period: '/mois',
    items: ['Dashcam : séquences conservées 24 h', 'Boutons 17 / 18 : séquences conservées 90 jours', 'Position associée à chaque session', 'Envoi sécurisé sur nos serveurs (UE)', 'Historique de vos sessions'],
    cta: 'Commencer gratuitement', href: '/inscription',
  },
  paid: {
    badge: 'Recommandé', name: 'Abonné CIVIK', price: '10€', period: '/mois',
    items: ['Tout le plan gratuit', 'Professionnels de sécurité privée partenaires informés à proximité', 'Suivi de ceux qui se déclarent en route', 'Rétention étendue des séquences', 'Historique enrichi'],
    cta: 'Créer mon compte', href: '/inscription',
  },
  /* Décision Marc 15/09 : tout passe par le site pour les abonnements — les apps ne gèrent pas les paiements et sont gratuites. */
  note: "L'abonnement se souscrit et se gère depuis votre espace CIVIK sur ce site. Les applications mobiles sont gratuites et n'effectuent aucun paiement.",
};

/* Correction 15/09 (soir) §4–8 : parcours numérique de candidature professionnelle. */
export const CANDIDATURE_URL = '/candidature';

export const PROS = {
  id: 'professionnels',
  title: 'Professionnels',
  sub: 'Rejoignez le réseau des professionnels partenaires CIVIK.',
  intro: 'Agents de sécurité, professionnels de la prévention, secouristes et professionnels de santé : candidatez directement en ligne pour rejoindre le réseau CIVIK.',
  cta: 'CANDIDATER EN LIGNE',
  ctaHref: CANDIDATURE_URL,
  steps: [
    { num: '1', title: 'Candidatez', text: 'Créez votre candidature et renseignez le questionnaire professionnel en ligne.' },
    { num: '2', title: 'Formez-vous', text: 'Suivez la formation CIVIK directement en ligne.' },
    { num: '3', title: 'Transmettez vos justificatifs', text: "Déposez en ligne vos diplômes et les documents demandés pour l'étude de votre candidature." },
    { num: '4', title: 'CIVIK étudie votre dossier', text: 'Votre candidature et vos justificatifs sont instruits par CIVIK.' },
    { num: '5', title: 'Rejoignez le réseau', text: 'Après validation de votre candidature, vous pouvez accéder aux fonctionnalités prévues pour les professionnels partenaires CIVIK.' },
  ],
  profiles: [
    { icon: 'shield', key: 'securite', title: 'Sécurité & prévention', text: 'Agents de sécurité privée et professionnels de la prévention peuvent candidater pour rejoindre le réseau CIVIK et accéder aux fonctionnalités correspondant à leur profil après validation de leur dossier.', cta: 'CANDIDATER', href: `${CANDIDATURE_URL}?profil=securite` },
    { icon: 'health', key: 'sante', title: 'Santé & secours', text: 'Secouristes et professionnels de santé peuvent candidater pour rejoindre le réseau CIVIK et accéder aux fonctionnalités correspondant à leur profil après validation de leur dossier.', cta: 'CANDIDATER', href: `${CANDIDATURE_URL}?profil=sante` },
  ],
  network: {
    title: 'Un réseau mobilisable à proximité',
    p1: "Lorsqu'une sollicitation correspond à leur profil et à leur zone, les professionnels partenaires concernés peuvent être informés simultanément.",
    p2: 'Les modalités applicables aux interventions et, le cas échéant, à leur rémunération sont précisées dans les conditions partenaires CIVIK.',
  },
};

export const USE_CASES = {
  id: 'cas-usage',
  title: "Cas d'usage",
  cases: [
    { icon: 'alert', title: 'Agression', text: "Filmez la scène et composez le 17 depuis l'application. Abonné : les professionnels partenaires proches sont informés." },
    { icon: 'car', title: 'Accident de la route', text: 'Dashcam active = preuve immédiate, horodatée et géolocalisée, déjà sur nos serveurs.' },
    { icon: 'health', title: 'Urgence médicale', text: 'Appuyez sur 18 : la situation est filmée et localisée pendant que vous appelez les secours.' },
    { icon: 'eye', title: 'Témoin', text: 'Vous assistez à un incident ? Enregistrez ; vous pourrez transmettre la séquence aux autorités si elles la demandent.' },
    { icon: 'phone', title: 'Preuve vidéo', text: 'Chaque enregistrement est horodaté, géolocalisé et conservé avec son empreinte SHA-256.' },
  ],
};

/* Lien d'installation Android (build EAS "preview" du 2026-09-02) — inchangé. */
export const ANDROID_INSTALL_URL = 'https://expo.dev/accounts/civik.tech/projects/civik/builds/5ec43c60-cea8-4585-bbd6-a51fe084d15f';

export const DOWNLOAD = {
  id: 'telecharger',
  title: 'Télécharger CIVIK',
  sub: "Applications iOS et Android en cours de publication sur les stores. L'espace web reste accessible.",
  qrTitle: 'Application Android — installation directe',
  qrHint: 'Scannez ce QR code avec votre téléphone Android, ou :',
  qrBtn: "Télécharger l'application Android ICI",
  note: "Android vous demandera d'autoriser l'installation depuis cette source. iOS (App Store) : bientôt disponible.",
  appStore: 'App Store — bientôt',
  playStore: 'Google Play — bientôt',
  webapp: 'Espace web',
};

/* ── Formulaire collectivités (cahier des charges §27) ── */
export const COLLECTIVITE_FORM = {
  fields: [
    { name: 'lastName', label: 'Nom', autoComplete: 'family-name', required: true },
    { name: 'firstName', label: 'Prénom', autoComplete: 'given-name', required: true },
    { name: 'role', label: 'Fonction', autoComplete: 'organization-title', required: true },
    { name: 'organization', label: 'Collectivité / organisme', autoComplete: 'organization', required: true },
    { name: 'postalCode', label: 'Code postal', autoComplete: 'postal-code', required: true, inputMode: 'numeric', pattern: '[0-9]{5}' },
    { name: 'city', label: 'Commune', autoComplete: 'address-level2', required: true },
    { name: 'email', label: 'E-mail professionnel', autoComplete: 'email', type: 'email', required: true },
    { name: 'phone', label: 'Téléphone', autoComplete: 'tel', type: 'tel', required: true },
    { name: 'population', label: "Nombre d'habitants", inputMode: 'numeric', required: true },
  ],
  need: { name: 'need', label: 'Votre besoin', required: true },
  consent: "J'ai pris connaissance de la politique de confidentialité et des informations relatives au traitement de mes données.",
  cta: 'ÊTRE CONTACTÉ PAR CIVIK',
  success: 'Votre demande a bien été transmise à CIVIK.',
  error: "Votre demande n'a pas pu être envoyée. Vous pouvez nous écrire à ",
};
